import React from 'react';
import Helmet from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Modal from 'antd/es/modal/Modal';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import { rest } from '@app/components/Api';
import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';
import LinkMore from '@app/components/LocalCommon/LinkMore/LinkMore';
import { EmailShare, FacebookShare, TwitterShare } from '@app/components/LocalCommon/ShareButtons/ShareButtons';
import { validateRequiredString } from '@app/components/LocalCommon/Utils/Validators';
import { bodyOverflowTransform } from '@app/components/LocalCommon/Utils/bodyOverflowTransform';
import PetMemorialGallery from '@app/components/Pages/PetMemorial/PetMemorialModalGallery';
import { dateConvert } from '@app/components/Pages/PetMemorial/utils/dateConvert';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import CustomReCAPTCHA from '@app/components/UI/CustomReCAPTCHA/CustomReCAPTCHA';
import NavSelectMobile from '@app/components/UI/NavSelectMobile/NavSelectMobile';
import { TesAuthorCredits } from '@app/components/UI/VetTesCard/VetTesCard';
import { useIsMobile } from '@app/hooks/useIsMobile';
import candleImg from '@app/images/link-icons/candle.svg';
import {
	MemorialCandle,
	MemorialsRes,
	PetMemModal,
	PetModalProps,
} from '@app/objects/PetMemorial';

const validationSchema = Yup.object({
	name: validateRequiredString('Full Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const PetMemorialModal: React.FC<PetMemModal> = ({ modalVisible, setModalVisible }) => {
	const context = useItemProviderContext<MemorialsRes>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { item, loading },
	} = context;

	const navigate = useNavigate();
	const location = useLocation();
	const params: { id?: string | undefined } = useParams();

	const initialModalProps = {
		memorial: {
			petName: '',
			birthday: 0,
			dateCreated: 0,
			dateOfPassing: 0,
			message: '',
			story: '',
			firstName: '',
			lastName: '',
			city: '',
			state: '',
			id: 0,
			images: [],
			testimonial: undefined,
			memorialCandles: [],
		},
		dateBirth: '',
		datePass: '',
		datePost: { month: '', day: 0, year: 0 },
	};

	const [modalProps, setModalProps] = React.useState<PetModalProps>(initialModalProps);
	const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);

	const navItems = [
		{ name: 'memory', title: `In loving memory of ${modalProps.memorial && modalProps.memorial.petName}` },
		{ name: 'about', title: `${modalProps.memorial && modalProps.memorial.petName}'s Story` },
		{ name: 'testimonial', title: 'Vet Testimonial', hidden: !modalProps.memorial.testimonial?.user && true },
	];

	const [currentIndex, setCurrentIndex] = React.useState(0);
	const currentIssue = navItems[currentIndex].name;

	const navCollapse = useIsMobile();
	const [sending, setSending] = React.useState(false);
	const [sent, setSent] = React.useState(false);
	const candleForm = React.createRef<HTMLDivElement>();

	React.useEffect(() => {
		if (item) {
			navigate(`/pet-memorial/${item.id}`);

			setModalProps({
				memorial: item,
				dateBirth: dateConvert(item).dateBirth,
				datePass: dateConvert(item).datePass,
				datePost: dateConvert(item).datePost,
			});

			setModalVisible(true);
		}
	}, [item]);

	const onSetIssue = (index) => {
		let num = index;

		if (navCollapse) {
			navItems.forEach((item, i: number) => {
				if (item.title === index) {
					num = i;
				}
			});
		}

		setCurrentIndex(num);
	};

	const closeModal = () => {
		navigate('/pet-memorial');
		setModalVisible(false);
		setModalProps(initialModalProps);
	};

	const scrollToCandleForm = () => candleForm.current?.scrollIntoView({ behavior: 'smooth' });

	const toDate = (date: number) => {
		const dateObj = new Date(date);

		return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
	};

	const sortByDate = (a: MemorialCandle, b: MemorialCandle) => {
		if (a.dateCreated < b.dateCreated) {
			return 1;
		}
		if (a.dateCreated > b.dateCreated) {
			return -1;
		}
		return 0;
	};

	React.useEffect(() => {
		setCurrentIndex(0);
	}, [modalProps.memorial.testimonial]);

	return (
		<Formik
			onSubmit={(values) => {
				if (isConfirmed) {
					values.memorialId = item.id;
					setSending(true);

					rest.post('v1/memorials/candle', values)
						.then(() => {
							setSent(true);
						})
						.finally(() => {
							setSending(false);
						});
				}
			}}
			initialValues={{
				name: '',
				email: '',
				message: '',
				memorialId: -1,
				captcha: '',
			}}
			validationSchema={validationSchema}
		>
			{(formikBag: FormikProps<any>) => {
				return (
					<>
						<Modal
							wrapClassName="pet-mem-modal pet-mem-modal-gallery modal-wrap"
							open={modalVisible}
							onOk={() => setModalVisible(false)}
							onCancel={() => {
								closeModal();
							}}
							width={1037}
							getContainer="#pet-memorial"
						>
							{!loading ? (
								<>
									<Helmet
										title={
											params.id
												? `In loving memorial of ${modalProps.memorial.petName} | Lap of Love`
												: 'Pet Memorials | Lap of Love'
										}
									/>
									{modalProps.memorial.id !== 0 && (
										<div className="pet-mem-modal__content">
											<div className="pet-mem-modal__container">
												<header className="pet-mem-modal__content-header">
													<div className="pet-mem-modal__content-header-title">
														<h4>{modalProps.memorial.petName}</h4>
														<div>
															<span>{modalProps.dateBirth}</span>
															{modalProps.dateBirth && <span> - </span>}
															<span>{modalProps.datePass || '...'}</span>
														</div>
													</div>
													<div className="pet-mem-modal__share-icons header-share-icons">
														<FacebookShare url={location.pathname} />
														<EmailShare url={location.pathname} />
														<TwitterShare url={location.pathname} />
													</div>
													<div className="pet-mem-modal__content-header-nav">
														{modalProps.memorial.memorialCandles?.length > 0 && (
															<div className="pet-mem-modal__content-header-candle">
																<img src={candleImg} alt="candle" />
																<span>{modalProps.memorial.memorialCandles.length}</span>
															</div>
														)}
														<button className="btn btn-sm btn-primary" onClick={scrollToCandleForm}>
															Light a candle
														</button>
													</div>
												</header>
												<PetMemorialGallery navCollapse={navCollapse} modalProps={modalProps} />
												{navCollapse ? (
													<div className="nav-select-mobile__wrapper">
														<button className="btn btn-primary" onClick={scrollToCandleForm}>
															Light a candle
														</button>
														<NavSelectMobile navItems={navItems} onChange={onSetIssue} currentIndex={currentIndex} />
														<div className="pet-mem-modal__share-icons wrapper-share-icons">
															<FacebookShare url={location.pathname} />
															<EmailShare url={location.pathname} />
															<TwitterShare url={location.pathname} />
														</div>
													</div>
												) : (
													<div className="pet-mem-modal__content-nav">
														{navItems && (
															navItems
																.filter((item) => !item.hidden)
																.map((item, index) => (
																	<button
																		key={index}
																		className={
																			currentIndex === index
																				? 'pet-mem-modal__content-nav-button active'
																				: 'pet-mem-modal__content-nav-button'
																		}
																		onClick={() => onSetIssue(index)}
																	>
																		{item.title}
																	</button>
																))
														)}
													</div>
												)}
												<div className="pet-mem-modal__content-issues">
													<div className="pet-mem-modal__content-topic">
														{currentIssue === 'memory' ? (
															<p>{modalProps.memorial && modalProps.memorial.message}</p>
														) : currentIssue === 'about' ? (
															<p>{modalProps.memorial && modalProps.memorial.story}</p>
														) : (
															<div className="pet-mem-modal__content-topic-tes">
																<div className="pet-mem-modal__content-topic-tes-name">
																	<CustomAvatar
																		src={modalProps?.memorial?.testimonial?.user?.avatar}
																		alt="vet"
																		maxWidth={100}
																		maxHeight={100}
																	/>
																	<span>{`Dr. ${modalProps.memorial.testimonial?.user?.firstName}`}</span>
																</div>
																<p>{modalProps.memorial.testimonial?.message}</p>
																<LinkMore
																	linkMoreCaption={`View Dr. ${modalProps.memorial.testimonial?.user?.firstName}'s biography`}
																	// function is used directly because using setModalVisible will not fire
																	// the hook while leaving the memorials page
																	handleCLick={() => bodyOverflowTransform(false)}
																	linkMoreLink={`/find-a-vet/${modalProps.memorial.state?.replace(' ', '-')}/${
																		modalProps.memorial.testimonial?.user?.areaUrl
																	}/veterinarians?veterinarian=${modalProps.memorial.testimonial?.user?.id}`}
																/>
															</div>
														)}
													</div>
													<TesAuthorCredits
														firstName={modalProps.memorial.firstName}
														lastName={modalProps.memorial.lastName}
														city={modalProps.memorial.city}
														state={modalProps.memorial.state}
														date={modalProps.datePost}
													/>
												</div>
												<div className="pet-mem-modal__content-comments">
													{modalProps.memorial.memorialCandles.sort(sortByDate).map((item, index) => {
														const newArray = item.message ? item.message.split('\n') : [];

														return (
															<div key={index} className="pet-mem-modal__content-comments-item">
																<div className="pet-mem-modal__content-comments-item-main">
																	<div>
																		{newArray.map((paragraph, index) => (
																			<p key={index}>{paragraph}</p>
																		))}
																	</div>
																	<img src={candleImg} alt="candle" />
																</div>
																<div>
																	<span>{item.firstName}</span>
																	<span> - </span>
																	<span>{toDate(item.dateCreated)}</span>
																</div>
															</div>
														);
													})}
												</div>
											</div>
										</div>
									)}
									<div className="pet-mem-modal__container">
										<div className="pet-mem-modal__content-feedback" ref={candleForm}>
											{sent ? (
												<div className="pet-mem-modal__content-feedback__confirmation">
													<h3>Thank you for lighting the candle</h3>
													<h4>
														We will post your message within 5 business days. You and the family will receive an email when your
														candle is ready to view.
													</h4>
												</div>
											) : (
												<>
													<header>
														<h5>{`Light a candle for ${modalProps.memorial.petName}`}</h5>
													</header>
													<Form autoComplete="off">
														<div className="pet-mem-modal__content-feedback-inputs">
															<CustomInput name="name" placeholder="Full Name*" formikBag={formikBag} />
															<CustomInput name="email" placeholder="Email Address*" formikBag={formikBag} removeSpace />
														</div>
														<div className="vet-tes-modal__form-textarea">
															<CustomInput
																component="textarea"
																name="message"
																placeholder="Your message"
																formikBag={formikBag}
															/>
														</div>
														<FormikField
															containerClassName="captcha"
															fieldName="captcha"
															render={(fieldProps) => (
																<CustomReCAPTCHA
																	formikBag={formikBag}
																	onChange={(value) => {
																		if (value) {
																			formikBag.setFieldValue('captcha', value);
																			setIsConfirmed(true);
																		} else {
																			formikBag.setFieldValue('captcha', '');
																			setIsConfirmed(false);
																			setSending(false);
																		}
																	}}
																/>
															)}
														/>
														<div className="pet-mem-modal__content-feedback-submit">
															<button type="submit" className="btn btn-md btn-primary" disabled={sending}>
																Light your candle
															</button>
															{sending && (
																<div className="lds-ring">
																	<div />
																	<div />
																	<div />
																	<div />
																</div>
															)}
														</div>
													</Form>
												</>
											)}
										</div>
									</div>
								</>
							) : (
								<BounceLoader />
							)}
						</Modal>
					</>
				);
			}}
		</Formik>
	);
};

export default PetMemorialModal;
